import _ from 'lodash'
import { BaseAction, State, selectCampaignTemplates } from './index'
import {
    CampaignTemplatesResult,
    TemplateDetail,
    TemplateData,
    CampaignInfo,
    CampaignSchedule,
    CampaignTaskElements,
    UpdateCampaignInfo,
    UpdateCampaignSchedule,
    UpdateCampaignTaskElementsRequest,
    TemplateDetailRequest,
    TemplatePaymentSummaryData,
    UpdatePaymentSummaryPayload,
    UpdateTemplateDataRequest,
    GetPaymentSummaryPayload,
} from '../services/api/campaignTemplates'
import { PlatformContentTypeFilter, Country } from 'services/api/constants'
import { MediaData } from 'components/MediaViewer'
import { TextListGroupValue } from 'particles/TextInputs/TextList/Group'
import { selectReviewTextImageFirstStepData } from './appPreview/selector/reviewTextImage'
import { selectDownloadAppFirstStepData } from './appPreview/selector/downloadApp'
import { selectFollowFirstStepData } from './appPreview/selector/follow'

export const OBJECTIVE = {
    credibility: 13,
    awareness: 14,
    traffic: 15,
    appPromotion: 16,
    content: 17,
    // others: 999 //since backend support others type,  enable this if we need to support others objective in the future
} as const

export type ObjectiveType = keyof typeof OBJECTIVE
export type Objective = (typeof OBJECTIVE)[ObjectiveType]

export interface CampaignTemplateState {
    uuid: string
    country: Country
    name: string
    objective: Objective
    description: string
    kpi_range: {
        min: string
        max: string
    }
    duration: string
}

export interface TemplateFilter {
    country?: Country | null
    brandUuid?: string | null
    objective?: Objective | null
    get_product_required?: boolean | null
    platform_content_types?: PlatformContentTypeFilter[] | null
    templateUuid?: string | null
    // average_product_price?: number | null // todo: enable this in mileston 2b
}

interface ElementId {
    uuid: string
}

interface TemplateTaskElement {
    element: ElementId
    [key: string]: string[] | boolean | number | MediaData[] | ElementId | TextListGroupValue[]
}

type CampaignTemplateEntitiesState = {
    [uuid: string]: CampaignTemplateState
}

type CampaignTemplateDataEntitiesState = {
    [uuid: string]: TemplateData
}

export type CampaignTemplatesState = {
    byId: {
        allTemplates: CampaignTemplateEntitiesState
        filteredTemplates: CampaignTemplateEntitiesState
        guidedTemplates: CampaignTemplateDataEntitiesState
    }
    allTemplates: string[]
    guidedTemplates: string[]
    recommendedGuidedTemplates: TemplateData[]
    filteredTemplates: string[]
    templateFilter: TemplateFilter
    templateData?: TemplateData
    templateDetail?: TemplateDetail
    templateTasks: TemplateTaskElement[]
    campaignInfo?: CampaignInfo
    campaignSchedule?: CampaignSchedule
    paymentSummary?: TemplatePaymentSummaryData
    brandsCountries?: Country[]
}

const initialState: CampaignTemplatesState = {
    byId: {
        allTemplates: {},
        filteredTemplates: {},
        guidedTemplates: {},
    },
    allTemplates: [],
    guidedTemplates: [],
    recommendedGuidedTemplates: [],
    filteredTemplates: [],
    templateFilter: {},
    templateTasks: [],
    templateData: undefined, // Since the platform_content_types type differs between templateFilter and templateData, it’s better to separate the frontend selected filter and the backend saved filter data.
    templateDetail: undefined,
    campaignInfo: undefined,
    campaignSchedule: undefined,
    paymentSummary: undefined,
    brandsCountries: undefined,
}

export enum CampaignTemplatesActions {
    CAMPAIGN_TEMPLATES_REQUEST = 'campaignTemplates/CAMPAIGN_TEMPLATES_REQUEST',
    SAVE_ALL_TEMPLATES = 'campaignTemplates/SAVE_ALL_TEMPLATES',
    SAVE_FILTERED_TEMPLATES = 'campaignTemplates/SAVE_FILTERED_TEMPLATES',
    CREATE_CAMPAIGN_BY_TEMPLATE = 'campaignTemplates/CREATE_CAMPAIGN_BY_TEMPLATE',
    UPDATE_TEMPLATE_DATA = 'campaignTemplates/UPDATE_TEMPLATE_DATA',
    SAVE_TEMPLATE_DATA = 'campaignTemplates/SAVE_TEMPLATE_DATA',
    SET_TEMPLATE_FILTER = 'campaignTemplates/SET_TEMPLATE_FILTER',
    CLEAR_TEMPLATE_FILTER = 'campaignTemplates/CLEAR_TEMPLATE_FILTER',
    TEMPLATE_DETAIL_REQUEST = 'campaignTemplates/TEMPLATE_DETAIL',
    SAVE_TEMPLATE_DETAIL = 'campaignTemplates/SAVE_TEMPLATE_DETAIL',
    CLEAR_TEMPLATE_DETAIL = 'campaignTemplates/CLEAR_TEMPLATE_DETAIL',
    TEMPLATE_DATA_REQUEST = 'campaignTemplates/TEMPLATE_FILTERS_REQUEST',
    CLEAR_TEMPLATE_DATA = 'campaignTemplates/CLEAR_TEMPLATE_DATA',
    SWITCH_TEMPLATE = 'campaignTemplates/SWITCH_TEMPLATE',
    CAMPAIGN_INFO_REQUEST = 'campaignTemplates/CAMPAIGN_INFO_REQUEST',
    SAVE_CAMPAIGN_INFO = 'campaignTemplates/SAVE_CAMPAIGN_INFO',
    CAMPAIGN_SCHEDULE_REQUEST = 'campaignTemplates/CAMPAIGN_SCHEDULE_REQUEST',
    CAMPAIGN_TASK_ELEMENTS_REQUEST = 'campaignTemplates/CAMPAIGN_TASK_ELEMENTS_REQUEST',
    SAVE_CAMPAIGN_SCHEDULE = 'campaignTemplates/SAVE_CAMPAIGN_SCHEDULE',
    SAVE_CAMPAIGN_TASK_ELEMENTS = 'campaignTemplates/SAVE_CAMPAIGN_TASK_ELEMENTS',
    UPDATE_CAMPAIGN_INFO = 'campaignTemplates/UPDATE_CAMPAIGN_INFO',
    CLEAR_CAMPAIGN_INFO = 'campaignTemplates/CLEAR_CAMPAIGN_INFO',
    UPDATE_CAMPAIGN_SCHEDULE = 'campaignTemplates/UPDATE_CAMPAIGN_SCHEDULE',
    CLEAR_CAMPAIGN_SCHEDULE = 'campaignTemplates/CLEAR_CAMPAIGN_SCHEDULE',
    UPDATE_CAMPAIGN_TASK_ELEMENTS = 'campaignTemplates/UPDATE_CAMPAIGN_TASK_ELEMENTS',
    CLEAR_CAMPAIGN_TASK_ELEMENTS = 'campaignTemplates/CLEAR_CAMPAIGN_TASK_ELEMENTS',
    SUBMIT_CAMPAIGN = 'campaignTemplates/SUBMIT_CAMPAIGN',
    UPDATE_TEMPLATE_UUID = 'campaignTemplates/UPDATE_TEMPLATE_UUID',
    GET_PAYMENT_SUMMARY = 'campaignTemplates/GET_PAYMENT_SUMMARY',
    CLEAR_PAYMENT_SUMMARY = 'campaignTemplates/CLEAR_PAYMENT_SUMMARY',
    UPDATE_PAYMENT_SUMMARY = 'campaignTemplates/UPDATE_PAYMENT_SUMMARY',
    SAVE_PAYMENT_SUMMARY = 'campaignTemplates/SAVE_PAYMENT_SUMMARY',
    SEND_TO_EDIT = 'campaignTemplates/SEND_TO_EDIT',
    REQUEST_PAYMENT = 'campaignTemplates/REQUEST_PAYMENT',
    SEND_TO_REVIEW = 'campaignTemplates/SEND_TO_REVIEW',
    PUBLISH_TEMPLATE = 'campaignTemplates/PUBLISH_TEMPLATE',
    EXPORT_TEMPLATE = 'campaignTemplates/EXPORT_TEMPLATE',
    PUBLISH_GUIDED_CAMPAIGN_TEMPLATE = 'campaignTemplates/PUBLISH_GUIDED_CAMPAIGN_TEMPLATE',
    UNPUBLISH_GUIDED_TEMPLATE = 'campaignTemplates/UNPUBLISH_GUIDED_TEMPLATE',
    USE_GUIDED_TEMPLATE = 'campaignTemplates/USE_GUIDED_TEMPLATE',
    DUPLICATE_GUIDED_TEMPLATE = 'campaignTemplates/DUPLICATE_GUIDED_TEMPLATE',
    GET_GUIDED_TEMPLATE_LIST = 'campaignTemplates/GET_GUIDED_TEMPLATE_LIST',
    SAVE_GUIDED_TEMPLATE_LIST = 'campaignTemplates/SAVE_GUIDED_TEMPLATE_LIST',
    CLEAR_GUIDED_TEMPLATE_LIST = 'campaignTemplates/CLEAR_GUIDED_TEMPLATE_LIST',
    GET_RECOMMENDED_GUIDED_TEMPLATE_LIST = 'campaignTemplates/GET_RECOMMENDED_GUIDED_TEMPLATE_LIST',
    SAVE_RECOMMENDED_GUIDED_TEMPLATE_LIST = 'campaignTemplates/SAVE_RECOMMENDED_GUIDED_TEMPLATE_LIST',
    CLEAR_RECOMMENDED_GUIDED_TEMPLATE_LIST = 'campaignTemplates/CLEAR_RECOMMENDED_GUIDED_TEMPLATE_LIST',
    UPDATE_GUIDED_TEMPLATE_PUBLISHED_STATUS = 'campaignTemplates/UPDATE_GUIDED_TEMPLATE_PUBLISHED_STATUS',
    UPDATE_GUIDED_TEMPLATE_DATA = 'campaignTemplates/UPDATE_GUIDED_TEMPLATE_DATA',
    DELETE_CAMPAIGN_TEMPLATE_DATA = 'campaignTemplates/DELETE_CAMPAIGN_TEMPLATE_DATA',
    REMOVE_GUIDED_TEMPLATE_DATA = 'campaignTemplates/REMOVE_GUIDED_TEMPLATE_DATA',
    BRANDS_COUNTRIES_REQUEST = 'campaignTemplates/BRANDS_COUNTRIES_REQUEST',
    SAVE_BRANDS_COUNTRIES = 'campaignTemplates/SAVE_BRANDS_COUNTRIES',
}

export enum CampaignTemplateStep {
    BRAND_AND_BUDGET = 'brandAndBudget',
    CAMPAIGN_REQUIREMENTS = 'campaignRequirements',
    TEMPLATE_SELECTION = 'templateSelection',
    CAMPAIGN_INFO = 'campaignInfo',
    CAMPAIGN_SCHEDULE = 'campaignSchedule',
    PURCHASE = 'purchase',
    IG_REELS = 'igReels',
    TIKTOK_REPOST = 'tiktokRepost',
    IG_STORY_REPOST = 'igStoryRepost',
    DOWNLOAD_APP = 'downloadApp',
    DOWNLOAD_APP_SIGN_UP_WITHOUT_ID = 'downloadAppSignUpWithoutId',
    IG_POST = 'igPost',
    IG_STORY_NON_UGC_STICKER_LINK = 'igStoryNonUgcStickerLink',
    TIKTOK = 'tiktok',
    REVIEW_TEXT_IMAGE_APPROVAL = 'reviewTextImageApproval',
    REVIEW_TEXT_IMAGE_APPROVAL_PURCHASE = 'reviewTextImageApprovalPurchase',
    PAYMENT_CONFIRMATION = 'paymentConfirmation',
    IG_STORY_UGC = 'igStoryUgc',
    REVIEW_TEXT_APPROVAL = 'reviewTextApproval',
    REVIEW_TEXT_NO_APPROVAL = 'reviewTextNoApproval',
    REVIEW_TEXT_NO_APPROVAL_PURCHASE = 'reviewTextNoApprovalPurchase',
    FOLLOW = 'follow',
    VIDEO_CREATION = 'videoCreation',
    PHOTO_CREATION = 'photoCreation',
}

export const CampaignTemplateStepValues = Object.values(CampaignTemplateStep)

export type TemplateQueryType = 'all' | 'filter'

export interface FilterParameters {
    country?: Country | null
    objective?: Objective | null
    get_product_required?: boolean | null
    platform_content_types?: string | null
    limit?: number
    // average_product_price?: number | null // todo: enable this in mileston 2b
}

export interface GetCampaignTemplatesPayload {
    type: TemplateQueryType
    filter: FilterParameters
}

export const getCampaignTemplates = (payload: GetCampaignTemplatesPayload) => ({
    payload,
    type: CampaignTemplatesActions.CAMPAIGN_TEMPLATES_REQUEST,
})

export interface GetTemplateDataPayload {
    templateDataUuid: string
}

export const getTemplateData = (payload: GetTemplateDataPayload) => ({
    payload,
    type: CampaignTemplatesActions.TEMPLATE_DATA_REQUEST,
})

interface SaveCampaignTemplatesPayload {
    data: CampaignTemplatesResult[] | undefined
}

export const saveAllTemplates = (payload: SaveCampaignTemplatesPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_ALL_TEMPLATES,
})

export const saveFilteredTemplates = (payload: SaveCampaignTemplatesPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_FILTERED_TEMPLATES,
})

export interface CreateCampaignByTemplatePayload {
    templateFilter: TemplateFilter
    title?: string
    description?: string
    filter_options?: Record<string, string | string[]>
    is_guided_flow?: boolean
    onSuccessCallback: (templateDataUuid: string, campaignUuid: string) => void
    onFailCallback: () => void
}

export const createCampaignByTemplate = (payload: CreateCampaignByTemplatePayload) => ({
    payload,
    type: CampaignTemplatesActions.CREATE_CAMPAIGN_BY_TEMPLATE,
})

export const updateTemplateData = (payload: UpdateTemplateDataRequest) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_TEMPLATE_DATA,
})

export interface SwitchTemplatePayload {
    templateDataUuid: string
    templateUuid: string
    campaignUuid: string
    onSuccessCallback: (templateDataUuid: string, campaignUuid: string) => void
}

export const switchTemplate = (payload: SwitchTemplatePayload) => ({
    payload,
    type: CampaignTemplatesActions.SWITCH_TEMPLATE,
})

export const setTemplateFilter = (payload: TemplateFilter) => ({
    payload,
    type: CampaignTemplatesActions.SET_TEMPLATE_FILTER,
})

export const clearTemplateFilter = () => ({
    type: CampaignTemplatesActions.CLEAR_TEMPLATE_FILTER,
})

export interface GetTemplateDetail {
    templateUuid: string
}

export const getTemplateDetail = (payload: GetTemplateDetail) => ({
    payload,
    type: CampaignTemplatesActions.TEMPLATE_DETAIL_REQUEST,
})

export const clearTemplateDetail = () => ({
    type: CampaignTemplatesActions.CLEAR_TEMPLATE_DETAIL,
})

interface SaveTemplateDetailPayload {
    data?: TemplateDetail
}

export const saveTemplateDetail = (payload: SaveTemplateDetailPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_TEMPLATE_DETAIL,
})

interface SaveTemplateDataPayload {
    data?: TemplateData
}

export const saveTemplateData = (payload: SaveTemplateDataPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_TEMPLATE_DATA,
})

interface UpdateTemplateUuidPayload {
    templateUuid: string
}

export const updateTemplateUuid = (payload: UpdateTemplateUuidPayload) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_TEMPLATE_UUID,
})

export const clearTemplateData = () => ({
    type: CampaignTemplatesActions.CLEAR_TEMPLATE_DATA,
})

interface GetCampaignInfo extends GetTemplateDataPayload {}

export const getCampaignInfo = (payload: GetCampaignInfo) => ({
    payload,
    type: CampaignTemplatesActions.CAMPAIGN_INFO_REQUEST,
})

export const updateCampaignInfo = (payload: UpdateCampaignInfo) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_CAMPAIGN_INFO,
})

export const clearCampaignInfo = () => ({
    type: CampaignTemplatesActions.CLEAR_CAMPAIGN_INFO,
})

interface SaveCampaignInfoPayload {
    data?: CampaignInfo
}

export const saveCampaignInfo = (payload: SaveCampaignInfoPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_CAMPAIGN_INFO,
})

interface GetCampaignSchedule extends GetTemplateDataPayload {
    start_time?: string | null
}

export const getCampaignSchedule = (payload: GetCampaignSchedule) => ({
    payload,
    type: CampaignTemplatesActions.CAMPAIGN_SCHEDULE_REQUEST,
})

export const updateCampaignSchedule = (payload: UpdateCampaignSchedule) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_CAMPAIGN_SCHEDULE,
})

export const clearCampaignSchedule = () => ({
    type: CampaignTemplatesActions.CLEAR_CAMPAIGN_SCHEDULE,
})

interface SaveCampaignSchedulePayload {
    data?: CampaignSchedule
}

export const saveCampaignSchedule = (payload: SaveCampaignSchedulePayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_CAMPAIGN_SCHEDULE,
})

interface GetCampaignTaskElements extends GetTemplateDataPayload {}

export const getCampaignTaskElements = (payload: GetCampaignTaskElements) => ({
    payload,
    type: CampaignTemplatesActions.CAMPAIGN_TASK_ELEMENTS_REQUEST,
})

export const saveCampaignTaskElements = (payload: CampaignTaskElements[]) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_CAMPAIGN_TASK_ELEMENTS,
})

export const updateCampaignTaskElements = (payload: UpdateCampaignTaskElementsRequest) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_CAMPAIGN_TASK_ELEMENTS,
})

export const clearCampaignTaskElements = () => ({
    type: CampaignTemplatesActions.CLEAR_CAMPAIGN_TASK_ELEMENTS,
})

export interface PayloadTemplateCallback extends TemplateDetailRequest {
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}

export const submitCampaignTemplate = (payload: PayloadTemplateCallback) => ({
    payload,
    type: CampaignTemplatesActions.SUBMIT_CAMPAIGN,
})

export const getPaymentSummary = (payload: GetPaymentSummaryPayload) => ({
    payload,
    type: CampaignTemplatesActions.GET_PAYMENT_SUMMARY,
})

export const clearPaymentSummary = () => ({
    type: CampaignTemplatesActions.CLEAR_PAYMENT_SUMMARY,
})

export const updatePaymentSummary = (payload: UpdatePaymentSummaryPayload) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_PAYMENT_SUMMARY,
})

export const saveTemplatePaymentSummary = (payload: TemplatePaymentSummaryData) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_PAYMENT_SUMMARY,
})

export const sendToEdit = (payload: PayloadTemplateCallback) => ({
    payload,
    type: CampaignTemplatesActions.SEND_TO_EDIT,
})

export const requestPayment = (payload: PayloadTemplateCallback) => ({
    payload,
    type: CampaignTemplatesActions.REQUEST_PAYMENT,
})

export const sendToReview = (payload: PayloadTemplateCallback) => ({
    payload,
    type: CampaignTemplatesActions.SEND_TO_REVIEW,
})

export const publishCampaignTemplate = (payload: PayloadTemplateCallback) => ({
    payload,
    type: CampaignTemplatesActions.PUBLISH_TEMPLATE,
})

export interface GuidedTemplatePublishPayload {
    templateUuid: string
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}

export const unpublishGuidedTemplate = (payload: GuidedTemplatePublishPayload) => ({
    payload,
    type: CampaignTemplatesActions.UNPUBLISH_GUIDED_TEMPLATE,
})

export interface UseGuidedTemplatePayload {
    templateDataUuid: string
    onSuccessCallback?: (templateDataUuid: string, campaignUuid: string) => void
    onErrorCallback?: () => void
}

export const useGuidedTemplate = (payload: UseGuidedTemplatePayload) => ({
    payload,
    type: CampaignTemplatesActions.USE_GUIDED_TEMPLATE,
})

export interface DuplicateGuidedTemplatePayload {
    templateDataUuid: string
    title?: string
    description?: string
    filter_options?: {
        business_challenges?: string[]
        industries?: string[]
        product_type?: string
        product_access_types?: string[]
        primary_business_challenge?: string
    }
    onSuccessCallback?: (templateDataUuid: string, campaignUuid: string) => void
    onErrorCallback?: () => void
}

export const duplicateGuidedTemplate = (payload: DuplicateGuidedTemplatePayload) => ({
    payload,
    type: CampaignTemplatesActions.DUPLICATE_GUIDED_TEMPLATE,
})

export interface PayloadExportTemplate extends PayloadTemplateCallback {
    campaignUuid: string
}

export const exportTemplate = (payload: PayloadExportTemplate) => ({
    payload,
    type: CampaignTemplatesActions.EXPORT_TEMPLATE,
})

export interface PublishGuidedTemplatePayload extends PayloadTemplateCallback {
    templateUuid: string
}

export const publishGuidedCampaignTemplate = (payload: PublishGuidedTemplatePayload) => ({
    payload,
    type: CampaignTemplatesActions.PUBLISH_GUIDED_CAMPAIGN_TEMPLATE,
})

export interface RequestGuidedTemplateListPayload {
    loadMore?: boolean
    keywords?: string | null
    country?: string
    team_type?: string[]
    industries: string
    business_challenges?: string
    product_type?: string
    product_access_types?: string
    platform_content_types?: string
}
export const getGuidedTemplateList = (payload: RequestGuidedTemplateListPayload) => ({
    payload,
    type: CampaignTemplatesActions.GET_GUIDED_TEMPLATE_LIST,
})

export const saveGuidedTemplateList = (payload: SaveCampaignTemplatesPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_GUIDED_TEMPLATE_LIST,
})

export const clearGuidedTemplateList = () => ({
    type: CampaignTemplatesActions.CLEAR_GUIDED_TEMPLATE_LIST,
})

export interface RequestRecommendedGuidedTemplateListPayload {
    keywords?: string | null
}

export const getRecommendedGuidedTemplateList = (payload: RequestRecommendedGuidedTemplateListPayload) => ({
    payload,
    type: CampaignTemplatesActions.GET_RECOMMENDED_GUIDED_TEMPLATE_LIST,
})

export interface SaveRecommendedGuidedTemplateListPayload {
    data: TemplateData[] | undefined
}

export const saveRecommendedGuidedTemplateList = (payload: SaveRecommendedGuidedTemplateListPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_RECOMMENDED_GUIDED_TEMPLATE_LIST,
})

export const clearRecommendedGuidedTemplateList = () => ({
    type: CampaignTemplatesActions.CLEAR_RECOMMENDED_GUIDED_TEMPLATE_LIST,
})

export interface UpdateGuidedTemplatePublishedStatusPayload {
    templateUuid: string
    isPublished: boolean
}

export const updateGuidedTemplatePublishedStatus = (payload: UpdateGuidedTemplatePublishedStatusPayload) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_GUIDED_TEMPLATE_PUBLISHED_STATUS,
})

export interface UpdateGuidedTemplateDataPayload {
    data: TemplateData
}

export const updateGuidedTemplateData = (payload: UpdateGuidedTemplateDataPayload) => ({
    payload,
    type: CampaignTemplatesActions.UPDATE_GUIDED_TEMPLATE_DATA,
})

export interface DeleteCampaignTemplateDataPayload {
    templateDataUuid: string
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}

export const deleteCampaignTemplateData = (payload: DeleteCampaignTemplateDataPayload) => ({
    payload,
    type: CampaignTemplatesActions.DELETE_CAMPAIGN_TEMPLATE_DATA,
})

export interface RemoveGuidedTemplateDataPayload {
    templateDataUuid: string
}

export const removeGuidedTemplateData = (payload: RemoveGuidedTemplateDataPayload) => ({
    payload,
    type: CampaignTemplatesActions.REMOVE_GUIDED_TEMPLATE_DATA,
})
export const getBrandsCountries = () => ({
    type: CampaignTemplatesActions.BRANDS_COUNTRIES_REQUEST,
})

export interface SaveBrandsCountriesPayload {
    countries?: Country[]
}

export const saveBrandsCountries = (payload: SaveBrandsCountriesPayload) => ({
    payload,
    type: CampaignTemplatesActions.SAVE_BRANDS_COUNTRIES,
})

export const campaignTemplatesReducer = (state: CampaignTemplatesState = initialState, action: BaseAction) => {
    switch (action.type) {
        case CampaignTemplatesActions.SAVE_ALL_TEMPLATES: {
            const { data } = action.payload
            const uuids = data.map((template: CampaignTemplatesResult) => template.uuid)
            return Object.assign({}, state, {
                ...state,
                allTemplates: _.uniq([...uuids]),
                byId: {
                    ...state.byId,
                    allTemplates: data.reduce(
                        (memo: CampaignTemplateEntitiesState, template: CampaignTemplatesResult) =>
                            Object.assign({}, memo, { [template.uuid]: template }),
                        state.byId.allTemplates,
                    ),
                },
            })
        }
        case CampaignTemplatesActions.SAVE_FILTERED_TEMPLATES: {
            const { data } = action.payload
            const uuids = data.map((template: CampaignTemplatesResult) => template.uuid)
            return Object.assign({}, state, {
                ...state,
                filteredTemplates: _.uniq([...uuids]),
                byId: {
                    ...state.byId,
                    filteredTemplates: data.reduce(
                        (memo: CampaignTemplateEntitiesState, template: CampaignTemplatesResult) =>
                            Object.assign({}, memo, { [template.uuid]: template }),
                        state.byId.filteredTemplates,
                    ),
                },
            })
        }
        case CampaignTemplatesActions.SAVE_GUIDED_TEMPLATE_LIST: {
            const { data } = action.payload
            const uuids = data.map((template: TemplateData) => template.uuid)
            return Object.assign({}, state, {
                guidedTemplates: _.uniq([...state.guidedTemplates, ...uuids]),
                byId: {
                    ...state.byId,
                    guidedTemplates: data.reduce(
                        (memo: CampaignTemplateEntitiesState, template: TemplateData) =>
                            Object.assign({}, memo, { [template.uuid]: template }),
                        state.byId.guidedTemplates,
                    ),
                },
            })
        }
        case CampaignTemplatesActions.CLEAR_GUIDED_TEMPLATE_LIST: {
            return Object.assign({}, state, {
                guidedTemplates: initialState.guidedTemplates,
                byId: {
                    ...state.byId,
                    guidedTemplates: initialState.byId.guidedTemplates,
                },
            })
        }
        case CampaignTemplatesActions.SAVE_RECOMMENDED_GUIDED_TEMPLATE_LIST: {
            const payloadData = action.payload.data as unknown
            const recommendedGuidedTemplates = Array.isArray(payloadData)
                ? payloadData
                : Array.isArray((payloadData as { results?: TemplateData[] })?.results)
                  ? ((payloadData as { results?: TemplateData[] }).results as TemplateData[])
                  : []

            return Object.assign({}, state, {
                recommendedGuidedTemplates,
            })
        }
        case CampaignTemplatesActions.CLEAR_RECOMMENDED_GUIDED_TEMPLATE_LIST: {
            return Object.assign({}, state, {
                recommendedGuidedTemplates: initialState.recommendedGuidedTemplates,
            })
        }
        case CampaignTemplatesActions.UPDATE_GUIDED_TEMPLATE_DATA: {
            const { data } = action.payload
            const current = state.byId.guidedTemplates[data.uuid]
            if (!current) return state
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    guidedTemplates: Object.assign({}, state.byId.guidedTemplates, {
                        [data.uuid]: { ...current, ...data },
                    }),
                },
            })
        }
        case CampaignTemplatesActions.UPDATE_GUIDED_TEMPLATE_PUBLISHED_STATUS: {
            const { templateUuid, isPublished } = action.payload
            const current = state.byId.guidedTemplates[templateUuid]
            if (!current) return state
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    guidedTemplates: Object.assign({}, state.byId.guidedTemplates, {
                        [templateUuid]: { ...current, is_published: isPublished },
                    }),
                },
            })
        }
        case CampaignTemplatesActions.REMOVE_GUIDED_TEMPLATE_DATA: {
            const { templateDataUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                guidedTemplates: state.guidedTemplates.filter(uuid => uuid !== templateDataUuid),
                byId: {
                    ...state.byId,
                    guidedTemplates: _.omit(state.byId.guidedTemplates, templateDataUuid),
                },
            })
        }
        case CampaignTemplatesActions.SET_TEMPLATE_FILTER: {
            return Object.assign({}, state, {
                ...state,
                templateFilter: {
                    ...state.templateFilter,
                    ...action.payload,
                },
            })
        }
        case CampaignTemplatesActions.CLEAR_TEMPLATE_FILTER: {
            return Object.assign({}, state, {
                ...state,
                templateFilter: initialState.templateFilter,
            })
        }
        case CampaignTemplatesActions.SAVE_TEMPLATE_DETAIL: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                templateDetail: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_TEMPLATE_DETAIL: {
            return Object.assign({}, state, {
                ...state,
                templateDetail: initialState.templateDetail,
            })
        }
        case CampaignTemplatesActions.SAVE_TEMPLATE_DATA: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                templateData: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_TEMPLATE_DATA: {
            return Object.assign({}, state, {
                ...state,
                templateData: initialState.templateData,
            })
        }
        case CampaignTemplatesActions.UPDATE_TEMPLATE_UUID: {
            const { templateUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                templateData: {
                    ...state.templateData,
                    template: {
                        uuid: templateUuid,
                    },
                },
            })
        }
        case CampaignTemplatesActions.SAVE_CAMPAIGN_INFO: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignInfo: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_CAMPAIGN_INFO: {
            return Object.assign({}, state, {
                ...state,
                campaignInfo: initialState.campaignInfo,
            })
        }
        case CampaignTemplatesActions.SAVE_CAMPAIGN_SCHEDULE: {
            const { data } = action.payload
            return Object.assign({}, state, {
                ...state,
                campaignSchedule: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_CAMPAIGN_SCHEDULE: {
            return Object.assign({}, state, {
                ...state,
                campaignSchedule: initialState.campaignSchedule,
            })
        }
        case CampaignTemplatesActions.SAVE_CAMPAIGN_TASK_ELEMENTS: {
            const data = action.payload
            return Object.assign({}, state, {
                ...state,
                templateTasks: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_CAMPAIGN_TASK_ELEMENTS: {
            return Object.assign({}, state, {
                ...state,
                templateTasks: initialState.templateTasks,
            })
        }
        case CampaignTemplatesActions.SAVE_PAYMENT_SUMMARY: {
            const data = action.payload
            return Object.assign({}, state, {
                ...state,
                paymentSummary: data,
            })
        }
        case CampaignTemplatesActions.CLEAR_PAYMENT_SUMMARY: {
            return Object.assign({}, state, {
                ...state,
                paymentSummary: initialState.paymentSummary,
            })
        }
        case CampaignTemplatesActions.SAVE_BRANDS_COUNTRIES: {
            const { countries } = action.payload
            return Object.assign({}, state, {
                ...state,
                brandsCountries: countries,
            })
        }
        default:
            return state
    }
}

export const selectTemplatesFilter = (state: State) => selectCampaignTemplates(state).templateFilter
export const selectTemplateData = (state: State) => selectCampaignTemplates(state).templateData
export const selectTemplateDetail = (state: State) => selectCampaignTemplates(state).templateDetail
export const selectCampaignInfo = (state: State) => selectCampaignTemplates(state).campaignInfo
export const selectCampaignSchedule = (state: State) => selectCampaignTemplates(state).campaignSchedule
export const selectCampaignTasks = (state: State) => selectCampaignTemplates(state).templateTasks
export const selectPaymentSummary = (state: State) => selectCampaignTemplates(state).paymentSummary
export const selectBrandsCountries = (state: State) => selectCampaignTemplates(state).brandsCountries
export const selectAllTemplates = (state: State) =>
    selectCampaignTemplates(state).allTemplates.map((uuid: string) => {
        const templateData = selectCampaignTemplates(state).byId.allTemplates[uuid]
        return { ...templateData }
    })

export const selectGuidedTemplates = (state: State) =>
    selectCampaignTemplates(state).guidedTemplates.map((uuid: string) => {
        const templateData = selectCampaignTemplates(state).byId.guidedTemplates[uuid]
        return { ...templateData }
    })
export const selectRecommendedGuidedTemplates = (state: State) => {
    const recommended = selectCampaignTemplates(state).recommendedGuidedTemplates
    return (recommended ?? []).map((templateData: TemplateData) => ({ ...templateData }))
}
export const selectFilteredTemplates = (state: State) =>
    selectCampaignTemplates(state).filteredTemplates.map((uuid: string) => {
        const templateData = selectCampaignTemplates(state).byId.filteredTemplates[uuid]
        return { ...templateData }
    })

export const selectPlaceholderFromTasks =
    (state: State) =>
    (placeholder: string): string | null => {
        const tasks = selectCampaignTasks(state)
        const task = tasks.find(item => item[placeholder] !== undefined)
        return task?.[placeholder].toString() ?? null
    }

export const selectReviewTextImagePlaceholders = (state: State) => {
    const reviewTextImageFirstStepData = selectReviewTextImageFirstStepData(state)
    const reviewPlatformFromDetail = selectPlaceholderFromTasks(state)('review_platform')
    const { review_platform: reviewPlatformFromPreview } = reviewTextImageFirstStepData

    const reviewPlatform = reviewPlatformFromPreview ? reviewPlatformFromPreview : reviewPlatformFromDetail
    return [reviewPlatform]
}

export const selectFollowPlaceholders = (state: State) => {
    const followFirstStepData = selectFollowFirstStepData(state)
    const followPlatformFromDetail = selectPlaceholderFromTasks(state)('follow_platform')
    const { follow_platform: followPlatformFromPreview } = followFirstStepData

    const followPlatform = followPlatformFromPreview ? followPlatformFromPreview : followPlatformFromDetail
    return [followPlatform]
}

export const selectDownloadAppPlaceholders = (state: State) => {
    const downloadAppFirstStepData = selectDownloadAppFirstStepData(state)
    const appNameFromDetail = selectPlaceholderFromTasks(state)('app_name')
    const { app_name: appNameFromPreview } = downloadAppFirstStepData

    const appName = appNameFromPreview ? appNameFromPreview : appNameFromDetail
    return [appName]
}
